import { defineStore } from 'pinia'
import { initState } from '@/utils/qiankun'
import { store } from '@/store'
import { userType } from './types'
import { routerArrays } from '@/layout/types'
import { router, resetRouter } from '@/router'
import { storageLocal, storageSession, toggleClass } from '@pureadmin/utils'
import { useMultiTagsStoreHook } from '@/store/modules/multiTags'
import { type DataInfo, setToken, removeToken, sessionKey } from '@/utils/auth'
import { v4 as uuidv4 } from 'uuid'
import { Md5 } from 'ts-md5'
import Watermark from '@/utils/watermark.js'
import {
  AuthLoginApi,
  AuthLoginParam,
  CurrentLoginUserVO
} from '@/api/modules/hall-hops-api'
import { useAppStoreHook } from './app'
import { getConfig } from '@/config'

// import { useDataThemeChange } from '@/layout/hooks/useDataThemeChange'
// const { setEpThemeColor } = useDataThemeChange()

export const useUserStore = defineStore({
  id: 'pure-user',
  state: (): userType => ({
    // 用户名
    username: storageSession().getItem<DataInfo>(sessionKey)?.userName ?? '',
    account: storageSession().getItem('account') ?? '',
    userId: storageSession().getItem('userId') ?? '',
    roles: storageSession().getItem('roles') ?? [],
    currentRole: storageSession().getItem('currentRole') ?? '',
    currentOrg: storageSession().getItem('currentOrg') ?? '',
    appInfo: storageSession().getItem('appInfo') ?? ''
  }),
  actions: {
    /** 存储用户id */
    SET_USERID(userId: string) {
      storageSession().setItem('userId', userId)
      this.userId = userId
    },
    /** 存储用户名 */
    SET_USERNAME(username: string) {
      this.username = username
    },
    /** 存储角色 */
    SET_ROLES(roles: Array<string>) {
      storageSession().setItem('roles', roles)
      this.roles = roles
    },
    /** 存储当前的roleId */
    SET_CURRENT_ROLE(roleId: string, roleName) {
      this.currentRole = { roleId, roleName }
      storageSession().setItem('currentRole', { roleId, roleName })
    },
    /** 存储当前的orgId */
    SET_CURRENT_ORG(orgId: string, orgName) {
      initState().setGlobalState({ currentOrg: { orgId, orgName } })
      storageSession().setItem('currentOrg', { orgId, orgName })
      this.currentOrg = {
        orgId,
        orgName
      }
      const username = storageSession().getItem<DataInfo>(sessionKey)?.userName
      const account = storageSession().getItem('account')
      Watermark.set(`${username} ${account} ${this.currentOrg.orgName}`)
    },
    SET_ACCOUNT(account: string) {
      this.account = account
      storageSession().setItem('account', account)
    },
    /**
     * 存储当前的appId
     */
    SET_APP_INFO(appId: string, appName: string, chnName: string) {
      storageSession().setItem('appInfo', {
        appId,
        appName,
        chnName
      })
      this.appInfo = {
        appId,
        appName,
        chnName
      }
    },
    /** 获取组织机构列表 */
    getOrgs() {
      return this.roles
        .filter((item) => item.id === this.currentRole.roleId)[0]
        .orgs.map((item) => {
          return {
            id: item.id,
            name: item.name
          }
        })
    },
    /** 登入 */
    loginByUsername(data, captchaToken) {
      const { userName, password } = data
      const uuid = uuidv4()
      const queryData: AuthLoginParam = {
        account: userName,
        random: uuid,
        token: Md5.hashStr(Md5.hashStr(password) + uuid),
        captchaToken
      }
      return new Promise<CurrentLoginUserVO>((resolve, reject) => {
        AuthLoginApi.loginV2(queryData).then((res) => {
          if (res.code === 200) {
            setToken(res.data.accessToken)
            const { userName, apps, account } = res.data
            storageSession().setItem(sessionKey, {
              userName,
              apps
            })
            this.SET_USERID(res.data.userId)

            Watermark.set(`${userName} ${account}`)
            storageSession().setItem('account', account)
            resolve(res.data)
          } else {
            reject(res)
          }
        })
      })
    },
    async logOut() {
      const version = sessionStorage.getItem('version')
      await AuthLoginApi.logOut()
      removeToken()
      this.SET_APP_INFO()
      storageLocal().clear()
      // storageSession().clear()
      const { Grey, Weak, MultiTagsCache, Layout } = getConfig()
      useAppStoreHook().setLayout(Layout)
      useMultiTagsStoreHook().multiTagsCacheChange(MultiTagsCache)
      toggleClass(Grey, 'html-grey', document.querySelector('html'))
      toggleClass(Weak, 'html-weakness', document.querySelector('html'))
      router.push('/login')
      useMultiTagsStoreHook().handleTags('equal', [...routerArrays])
      resetRouter()
      Watermark.out()
      sessionStorage.setItem('version', version)
    }
  }
})

export function useUserStoreHook() {
  return useUserStore(store)
}
